import React, { useEffect } from 'react';
import * as R from 'ramda';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { useSelector } from 'react-redux';

import { PosPlugin } from 'plugins/plugin';
import { getClientCode } from 'reducers/Login';
import { saveCustomer } from 'services/ErplyAPI';
import {
  getDefaultCustomerGroup,
  getSearchCustomerGroups,
  getUseCustomerRegistry,
} from 'reducers/customerSearch';

import {
  disableField,
  insertBefore,
  insertAfter,
  matches,
  notMatches,
  renameField,
  getCustomerPropertyFn,
} from '../utils';

export const UICustomerForm: PosPlugin['UICustomerForm'] = ({
  value,
  onChange,
  children,
}) => {
  const sampleField = React.Children.toArray(children).find((c: any) =>
    /firstName/.test(c?.key),
  );
  const customerGroups = useSelector(getSearchCustomerGroups);
  const defaultCustomerGroup = useSelector(getDefaultCustomerGroup);
  const defaultGroupID = (defaultCustomerGroup ?? customerGroups?.[0])
    ?.customerGroupID;
  const useCustomerRegistry = useSelector(getUseCustomerRegistry);
  const { getCustomerPropertyName } = useSelector(getCustomerPropertyFn);
  const birthNotePropName = getCustomerPropertyName('birthNote');
  const customerCardNumberPropName = getCustomerPropertyName(
    'customerCardNumber',
  );
  const codePropName = getCustomerPropertyName('code');
  const leadCodePropName = getCustomerPropertyName('leadCode');

  // Since group field is locked, ensure it has a legal value
  useEffect(() => {
    if (!value.groupID) {
      onChange(R.assoc('groupID', defaultGroupID));
    }
  }, [value.groupID, defaultGroupID, onChange]);
  // Since birthday field is hidden, ensure it has a legal value
  useEffect(() => {
    const [y, m, d] = value.birthday?.split('-') ?? [];
    if (![y, m, d].every(Number)) {
      onChange(R.assoc('birthday', '1970-01-01'));
    }
  }, [onChange, value.birthday]);
  // Since euCustomerType field is hidden, ensure it has a legal value
  useEffect(() => {
    if (!value.euCustomerType) {
      onChange(R.assoc('euCustomerType', 'DOMESTIC'));
    }
  }, [onChange, value.euCustomerType]);
  // Since gender field is hidden, ensure it has a legal value
  useEffect(() => {
    if (!value.gender && useCustomerRegistry) {
      onChange(R.assoc('gender', 'UNIDENTIFIED'));
    }
  }, [onChange, useCustomerRegistry, value.gender]);
  // On creating a new customer, assign the customerCardNumber to be equal to DX, where X is a random 5-digit long number
  useEffect(() => {
    if (!value.customerID) {
      onChange(
        R.assoc(
          customerCardNumberPropName,
          `D${Math.floor(Math.random() * 90000) + 10000}`,
        ),
      );
    }
  }, [customerCardNumberPropName, onChange, value.customerID]);
  // Form updates
  return R.pipe(
    R.map(
      R.pipe(
        R.when(matches(/groupID/), disableField),
        R.when(
          matches(new RegExp(customerCardNumberPropName)),
          R.pipe(
            disableField,
            renameField('Card number'),
            R.dissocPath(['props', 'label']),
          ),
        ),
      ),
    ),
    elements =>
      R.move(
        R.findIndex(matches(new RegExp(customerCardNumberPropName)), elements),
        R.findIndex(matches(/groupID/), elements),
        elements,
      ),
    // Remove fields birthday, euCustomerType, gender and id code
    R.filter(
      notMatches(new RegExp(`birthday|euCustomerType|gender|${codePropName}`)),
    ),
    insertBefore(
      new RegExp(customerCardNumberPropName),
      R.mergeDeepRight(sampleField ?? <div>Sample field not found</div>)({
        key: birthNotePropName,
        props: {
          title: 'Birth note',
          'data-testid': birthNotePropName,
          field: {
            key: birthNotePropName,
            validate: undefined,
          },
        },
      }),
    ),
    R.when(
      () => !useCustomerRegistry,
      insertAfter(
        /mobile/,
        R.mergeDeepRight(sampleField ?? <div>Sample field not found</div>)({
          key: leadCodePropName,
          props: {
            title: 'Lead code',
            'data-testid': leadCodePropName,
            field: {
              key: leadCodePropName,
              validate: undefined,
            },
          },
        }),
      ),
    ),
    // @ts-ignore
  )(React.Children.toArray(children));
};

export const onSaveCustomer: Required<
  Required<PosPlugin>['onSaveCustomer']
>['on'] = (p, ap) => async (_dispatch, getState) => {
  const { getCustomerPropertyName } = getCustomerPropertyFn(getState());
  return R.pipe(
    R.dissocPath(['attributes', 'wbuBirth']),
    R.assoc(getCustomerPropertyName('birthNote'), ap.attributes.wbuBirth),
  )(ap) as typeof ap;
};
export const updateCustomerCardNumber: Required<
  Required<PosPlugin>['onSaveCustomer']
>['after'] = (p, customerID) => async (
  dispatch: ThunkDispatch<any, any, Action>,
  getState,
) => {
  const { getCustomerPropertyName } = getCustomerPropertyFn(getState());
  const clientCode = getClientCode(getState());
  const customerNr = customerID.toString().padStart(7, '0');
  await saveCustomer({
    customerID,
    [getCustomerPropertyName(
      'customerCardNumber',
    )]: `${clientCode}-${customerNr}`,
  });
};
